/**
 * Local (device-only) datastore for Tracker Daily Money guest mode.
 * Mirrors the backend API contract so the same UI code can be used
 * whether the user is signed in (server) or not (local).
 *
 * All data lives under a single localStorage key. On sign-up, the whole
 * blob can be POSTed to /api/import/bulk to migrate to the account.
 */
const KEY = "trackerdaily_local_v1";

function nowIso() {
  return new Date().toISOString();
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "loc-" + Math.random().toString(36).slice(2) + "-" + Date.now();
}

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return { user: null, transactions: [], recurring: [], budget: null, category_budgets: [] };
}

function save(state) {
  localStorage.setItem(KEY, JSON.stringify(state));
}

export function hasLocalData() {
  const s = load();
  return !!(s.user || s.transactions?.length || s.recurring?.length || s.category_budgets?.length || s.budget);
}

export function initLocalUser({ name = "You", currency = "USD" } = {}) {
  const s = load();
  if (!s.user) {
    s.user = {
      id: "local",
      email: "",
      name,
      currency,
      plan: "free",
      subscription_status: null,
      current_period_end: null,
      created_at: nowIso(),
      mode: "local",
    };
    save(s);
  }
  return s.user;
}

export function clearLocal() {
  localStorage.removeItem(KEY);
}

export function exportLocalData() {
  const s = load();
  return {
    user: s.user,
    transactions: s.transactions,
    recurring: s.recurring,
    budget: s.budget,
    category_budgets: s.category_budgets,
  };
}

// ---------- Route handlers ----------
const DEFAULT_CATEGORIES = [
  "Salary","Freelance","Investment","Refund",
  "Groceries","Rent","Utilities","Dining","Transport",
  "Shopping","Entertainment","Health","Insurance","Education",
  "Travel","Gifts","Taxes","Subscriptions",
  "Savings","Loan","Donation","Other",
];

function summarize(transactions, startDate, endDate) {
  let money_in = 0, money_out = 0, count = 0;
  for (const t of transactions) {
    if (startDate && t.date < startDate) continue;
    if (endDate && t.date > endDate) continue;
    count++;
    if (t.type === "in") money_in += Number(t.amount || 0);
    else money_out += Number(t.amount || 0);
  }
  return {
    balance: Math.round((money_in - money_out) * 100) / 100,
    money_in: Math.round(money_in * 100) / 100,
    money_out: Math.round(money_out * 100) / 100,
    transaction_count: count,
  };
}

function currentMonthBounds() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const lastDay = new Date(y, m, 0).getDate();
  return {
    start: `${y}-${String(m).padStart(2, "0")}-01`,
    end: `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
    month: `${y}-${String(m).padStart(2, "0")}`,
    y,
    m,
  };
}

function matchPath(pattern, path) {
  // simple template: "/transactions/:id"
  const p = pattern.split("/").filter(Boolean);
  const q = path.split("?")[0].split("/").filter(Boolean);
  if (p.length !== q.length) return null;
  const params = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(":")) params[p[i].slice(1)] = decodeURIComponent(q[i]);
    else if (p[i] !== q[i]) return null;
  }
  return params;
}

function parseQuery(url) {
  const q = url.split("?")[1];
  if (!q) return {};
  return Object.fromEntries(new URLSearchParams(q).entries());
}

function ok(data) {
  return Promise.resolve({ data });
}

function err(status, detail) {
  const e = new Error(detail);
  e.response = { status, data: { detail } };
  return Promise.reject(e);
}

export function isLocalMode() {
  return typeof window !== "undefined" && localStorage.getItem("trackerdaily_local_mode") === "true";
}

export function enableLocalMode() {
  localStorage.setItem("trackerdaily_local_mode", "true");
  initLocalUser();
}

export function disableLocalMode() {
  localStorage.removeItem("trackerdaily_local_mode");
}

// ---------- Public: handle a request ----------
export async function handleLocal(method, url, body) {
  method = method.toUpperCase();
  const path = url.split("?")[0];
  const q = parseQuery(url);
  const s = load();

  // Auth
  if (method === "GET" && path === "/auth/me") {
    if (!s.user) return err(401, "Not authenticated");
    return ok(s.user);
  }
  if (method === "PATCH" && path === "/auth/me") {
    s.user = { ...s.user, ...body };
    save(s);
    return ok(s.user);
  }
  if (method === "POST" && (path === "/auth/logout" || path === "/auth/login" || path === "/auth/register")) {
    return err(400, "Not available in local mode");
  }

  // Features / plans / currencies
  if (method === "GET" && path === "/features") return ok({ subscription_enabled: false });
  if (method === "GET" && path === "/currencies") return ok({ currencies: ["USD","EUR","GBP","INR","JPY","AUD","CAD"] });
  if (method === "GET" && path === "/categories") {
    const used = Array.from(new Set(s.transactions.map((t) => t.category)));
    return ok({ categories: Array.from(new Set([...used, ...DEFAULT_CATEGORIES])) });
  }

  // Transactions
  if (method === "GET" && path === "/transactions") {
    let list = s.transactions.slice();
    if (q.start_date) list = list.filter((t) => t.date >= q.start_date);
    if (q.end_date) list = list.filter((t) => t.date <= q.end_date);
    if (q.category) list = list.filter((t) => t.category === q.category);
    if (q.type === "in" || q.type === "out") list = list.filter((t) => t.type === q.type);
    list.sort((a, b) => (a.date < b.date ? 1 : -1));
    if (q.limit) list = list.slice(0, Number(q.limit));
    return ok(list);
  }
  if (method === "POST" && path === "/transactions") {
    const t = {
      id: uuid(),
      type: body.type,
      amount: Number(body.amount),
      category: (body.category || "").trim(),
      note: (body.note || "").trim(),
      date: body.date,
      created_at: nowIso(),
    };
    s.transactions.push(t);
    save(s);
    return ok(t);
  }
  const patchTxn = matchPath("/transactions/:id", path);
  if (patchTxn && method === "PATCH") {
    const t = s.transactions.find((x) => x.id === patchTxn.id);
    if (!t) return err(404, "Transaction not found");
    Object.assign(t, body);
    save(s);
    return ok(t);
  }
  if (patchTxn && method === "DELETE") {
    s.transactions = s.transactions.filter((x) => x.id !== patchTxn.id);
    save(s);
    return ok({ ok: true });
  }

  // Summary
  if (method === "GET" && path === "/summary") {
    return ok(summarize(s.transactions, q.start_date, q.end_date));
  }
  if (method === "GET" && path === "/summary/monthly") {
    const year = Number(q.year) || new Date().getFullYear();
    const monthly = [];
    for (let m = 1; m <= 12; m++) {
      const start = `${year}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(year, m, 0).getDate();
      const end = `${year}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      const s2 = summarize(s.transactions, start, end);
      monthly.push({ date: `${year}-${String(m).padStart(2, "0")}`, money_in: s2.money_in, money_out: s2.money_out });
    }
    return ok(monthly);
  }

  // Budget
  if (method === "GET" && path === "/budget") {
    const cap = Number(s.budget?.monthly_cap || 0);
    const { start, end, month } = currentMonthBounds();
    let spent = 0;
    for (const t of s.transactions) {
      if (t.type === "out" && t.date >= start && t.date <= end) spent += Number(t.amount);
    }
    const remaining = Math.max(cap - spent, 0);
    const pct = cap > 0 ? Math.round((spent / cap) * 1000) / 10 : 0;
    let status = "ok";
    if (cap > 0 && spent >= cap) status = "over";
    else if (cap > 0 && spent >= 0.8 * cap) status = "warning";
    return ok({
      monthly_cap: Math.round(cap * 100) / 100,
      spent: Math.round(spent * 100) / 100,
      remaining: Math.round(remaining * 100) / 100,
      percent_used: pct,
      status,
      month,
    });
  }
  if (method === "PUT" && path === "/budget") {
    s.budget = { monthly_cap: Number(body.monthly_cap) };
    save(s);
    return handleLocal("GET", "/budget", null);
  }

  // Category budgets
  if (method === "GET" && path === "/budget/categories") {
    const { start, end } = currentMonthBounds();
    const spent = {};
    for (const t of s.transactions) {
      if (t.type !== "out") continue;
      if (t.date < start || t.date > end) continue;
      spent[t.category] = (spent[t.category] || 0) + Number(t.amount);
    }
    const list = s.category_budgets.map((cb) => {
      const cap = Number(cb.monthly_cap);
      const sp = spent[cb.category] || 0;
      const pct = cap > 0 ? Math.round((sp / cap) * 1000) / 10 : 0;
      let status = "ok";
      if (cap > 0 && sp >= cap) status = "over";
      else if (cap > 0 && sp >= 0.8 * cap) status = "warning";
      return {
        category: cb.category,
        monthly_cap: Math.round(cap * 100) / 100,
        spent: Math.round(sp * 100) / 100,
        remaining: Math.max(Math.round((cap - sp) * 100) / 100, 0),
        percent_used: pct,
        status,
      };
    });
    list.sort((a, b) => b.percent_used - a.percent_used);
    return ok(list);
  }
  const putCB = matchPath("/budget/categories/:cat", path);
  if (putCB && method === "PUT") {
    const existing = s.category_budgets.find((c) => c.category === putCB.cat);
    if (existing) existing.monthly_cap = Number(body.monthly_cap);
    else s.category_budgets.push({ category: putCB.cat, monthly_cap: Number(body.monthly_cap) });
    save(s);
    return ok({ ok: true });
  }
  if (putCB && method === "DELETE") {
    const before = s.category_budgets.length;
    s.category_budgets = s.category_budgets.filter((c) => c.category !== putCB.cat);
    if (s.category_budgets.length === before) return err(404, "Not found");
    save(s);
    return ok({ ok: true });
  }

  // Recurring
  if (method === "GET" && path === "/recurring") return ok(s.recurring);
  if (method === "POST" && path === "/recurring") {
    const r = {
      id: uuid(),
      type: body.type,
      amount: Number(body.amount),
      category: (body.category || "").trim(),
      note: (body.note || "").trim(),
      day_of_month: Number(body.day_of_month),
      active: !!body.active,
      last_posted_month: null,
      created_at: nowIso(),
    };
    s.recurring.unshift(r);
    save(s);
    return ok(r);
  }
  const recId = matchPath("/recurring/:id", path);
  if (recId && method === "PATCH") {
    const r = s.recurring.find((x) => x.id === recId.id);
    if (!r) return err(404, "Not found");
    Object.assign(r, body);
    save(s);
    return ok(r);
  }
  if (recId && method === "DELETE") {
    const before = s.recurring.length;
    s.recurring = s.recurring.filter((x) => x.id !== recId.id);
    if (s.recurring.length === before) return err(404, "Not found");
    save(s);
    return ok({ ok: true });
  }

  // Fallbacks not supported in local mode
  if (path.startsWith("/export/pdf")) return err(400, "PDF export requires an account. Sign up (free) to enable — your data will be migrated automatically.");
  if (path.startsWith("/import/csv")) return err(400, "CSV import is coming to local mode soon. For now, add transactions manually or sign up.");
  if (path.startsWith("/payments") || path.startsWith("/stripe") || path.startsWith("/billing")) return err(400, "Not available in local mode");

  return err(404, `Local mode: unsupported endpoint ${method} ${path}`);
}
